import { ChatMessagesProps } from '@/interfaces';
import clsx from 'clsx';
import parse from 'html-react-parser';
import * as React from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { Avatar, Icon, LoaderDots, Typography } from '@/components/common';
import { MessageReference } from '@/components/chat/MessageReference';
import { formatTimeWithToday } from '@/lib';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import Icons from '@/const/icons';
import { WrapperMessage } from './WrapperMessage';

export interface BaseChatMessageListProps {
	containerRef?: any;
	className?: string;
	messages: ChatMessagesProps[];
	streamingMessageId?: string | null;
	currentBotMessage?: string;
	isLoading?: boolean;
	applicationId?: number;
	conversationId?: string;
	caseId?: string;
	hideHeaderDate?: boolean;

	// Delegated behavior from hooks
	addChatMessages: any;
	date: Date | string | number;
	threadId?: any;
	user?: any;

	// UI customization flags
	showAvatarAndName?: boolean;
	showTimestampCondition?: (
		item: ChatMessagesProps,
		isStreamingMessage: boolean
	) => boolean;
	streamingLoaderBgClass?: string;
}

export const BaseChatMessageList: React.FC<BaseChatMessageListProps> = ({
	containerRef,
	className,
	messages,
	streamingMessageId,
	currentBotMessage,
	isLoading,
	applicationId,
	conversationId,
	caseId,
	hideHeaderDate = false,
	addChatMessages,
	date,
	threadId,
	// user,
	showAvatarAndName = true,
	showTimestampCondition = () => true,
	streamingLoaderBgClass = 'bg-grey-10',
}) => {
	const router = useRouter();

	const handleClick = async (
		event: React.MouseEvent<HTMLDivElement>
	): Promise<void> => {
		const target = event.target as HTMLElement;
		if (target.tagName === 'BUTTON') {
			if (target.hasAttribute('disabled')) {
				return;
			}
			const buttonContent = target.textContent?.trim() || '';
			await addChatMessages(buttonContent, 'user', threadId);
		}
	};

	const isHTMLContent = (content: string): boolean => {
		return /<\/?[a-z][\s\S]*>/i.test(content);
	};

	const renderMessage = (item: ChatMessagesProps, index: number) => {
		const isStreamingMessage = item.id === streamingMessageId;

		let messageContent = item.message;
		if (isStreamingMessage && currentBotMessage) {
			messageContent = currentBotMessage;
		}

		let mainContent = messageContent;

		const timestampMatch = messageContent.match(
			/<div class="chat-timestamp">.*?<\/div>$/
		);
		if (timestampMatch) {
			mainContent = messageContent.replace(timestampMatch[0], '');
		}

		const firstName = item.role === 'bot' ? 'ClaimMagic' : item.first_name;
		const lastName = item.role === 'bot' ? 'AI' : item.last_name;
		const profilePicture =
			item.role === 'bot' ? Icons.hatAvatar : item.profile_picture ?? '';

		if (item.referenced_message_id) {
			return (
				<React.Fragment key={item.id || 'chat-message-' + index}>
					<MessageReference
						id={item.id || ''}
						content={item.message}
						createdAt={item.timestamp || ''}
						firstname={firstName || ''}
						lastname={lastName || ''}
						profile_picture={profilePicture || ''}
						role={item.role as 'client' | 'lawyer' | 'system' | 'bot'}
						me={item.role === 'user'}
						referenced_message_id={item.referenced_message_id}
						referenced_message={item.referenced_message}
					/>
				</React.Fragment>
			);
		}

		if (item.action_required_id && item.role !== 'client') {
			return (
				<React.Fragment key={item.id || 'chat-message-' + index}>
					<MessageReference
						id={item.id || ''}
						content={item.message}
						createdAt={item.timestamp || ''}
						firstname={firstName || ''}
						lastname={lastName || ''}
						profile_picture={profilePicture || ''}
						role={item.role as 'client' | 'lawyer' | 'system' | 'bot'}
						me={item.role === 'user'}
						action_required_id={item.action_required_id}
						case_id={caseId}
						document_name={item.document_name}
						document_url={item.document_url}
						applicationId={applicationId}
						conversationId={conversationId}
						action_required_complete={item.action_required_complete}
						type={item.type}
					/>
				</React.Fragment>
			);
		}

		if (item.action_required_id && item.role === 'client') {
			return (
				<>
					{showAvatarAndName && (
						<div className="flex items-center gap-2 mb-1 justify-end">
							<Avatar
								photoUrl={profilePicture}
								firstname={firstName}
								lastname={lastName}
								size="small"
							/>
							<Typography type="semibold-14" className="text-black">
								{firstName + ' ' + lastName}
							</Typography>
						</div>
					)}
					<WrapperMessage item={item}>
						<div className="flex items-center gap-6">
							<Icon
								src={Icons.documentUpload}
								width={24}
								height={24}
								className="text-[#292D32] stroke-1"
							/>
							<div className="flex flex-col">
								{mainContent}
								<Typography type="regular-12" className="text-gray-35">
									Uploaded Successfully
								</Typography>
							</div>
							<Icon
								src={Icons.tickCircle}
								width={24}
								height={24}
								className="!text-green-60"
							/>
						</div>
					</WrapperMessage>
				</>
			);
		}

		return (
			<React.Fragment key={item.id || 'chat-message-' + index}>
				<div className="message-container text-sm">
					{showAvatarAndName && (
						<div
							className={clsx('flex items-center gap-2 mb-1', {
								'justify-end': item.role === 'user' || item.role === 'client',
							})}
						>
							<Avatar
								photoUrl={profilePicture}
								firstname={firstName}
								lastname={lastName}
								size="small"
							/>
							<Typography type="semibold-14" className="text-black">
								{firstName + ' ' + lastName}
							</Typography>
						</div>
					)}

					{isHTMLContent(mainContent) ? (
						(() => {
							const hasOwnWrapper =
								mainContent.includes('bg-grey-10') ||
								mainContent.includes('bg-green-20') ||
								mainContent.includes('bg-purple-35');

							if (hasOwnWrapper) {
								return parse(mainContent);
							}
							return (
								<WrapperMessage item={item}>
									<div className="html-content text-black whitespace-pre-wrap">
										{parse(mainContent)}
									</div>
								</WrapperMessage>
							);
						})()
					) : (
						<WrapperMessage item={item}>
							<div className="markdown-content">
								<ReactMarkdown
									components={{
										h1: ({ children }) => (
											<h1 className="text-xl font-bold mb-2">{children}</h1>
										),
										h2: ({ children }) => (
											<h2 className="text-lg font-bold mb-2">{children}</h2>
										),
										h3: ({ children }) => (
											<h3 className="text-base font-bold mb-1">{children}</h3>
										),
										p: ({ children }) => (
											<p className="text-black font-normal mb-2">{children}</p>
										),
										strong: ({ children }) => (
											<strong className="font-bold">{children}</strong>
										),
										em: ({ children }) => (
											<em className="italic">{children}</em>
										),
										ul: ({ children }) => (
											<ul className="list-disc pl-4 mb-2">{children}</ul>
										),
										ol: ({ children }) => (
											<ol className="list-decimal pl-4 mb-2">{children}</ol>
										),
										li: ({ children }) => <li className="mb-1">{children}</li>,
										code: ({ children }) => (
											<code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono">
												{children}
											</code>
										),
										pre: ({ children }) => (
											<pre className="bg-gray-100 p-3 rounded-lg overflow-x-auto mb-2">
												{children}
											</pre>
										),
										blockquote: ({ children }) => (
											<blockquote className="border-l-4 border-gray-300 pl-4 italic mb-2">
												{children}
											</blockquote>
										),
										a: ({ href, children }) => {
											const isInternalLink =
												href && (href.startsWith('/') || href.startsWith('.'));

											if (isInternalLink) {
												if (router.asPath === href) {
													return (
														<span className="text-current">{children}</span>
													);
												}

												return (
													<Link
														href={href}
														className="text-blue-link hover:underline"
													>
														{children}
													</Link>
												);
											}

											return (
												<a
													href={href}
													target="_blank"
													rel="noopener noreferrer"
													className="text-blue-link hover:underline"
												>
													{children}
												</a>
											);
										},
									}}
								>
									{mainContent}
								</ReactMarkdown>
							</div>
						</WrapperMessage>
					)}

					{item.timestamp &&
						showTimestampCondition(item, isStreamingMessage) && (
							<div
								className={clsx('mt-1', {
									'text-right': item.role === 'user' || item.role === 'client',
								})}
							>
								<Typography type="regular-12" className="text-opacity-black">
									{format(new Date(item.timestamp), 'MM/dd/yyyy, h:mm a')}
								</Typography>
							</div>
						)}
				</div>

				{isStreamingMessage && (
					<div
						className={clsx(
							'p-2 rounded-2xl w-fit rounded-tl-none mt-2',
							streamingLoaderBgClass
						)}
					>
						<div className="flex items-center space-x-2">
							<LoaderDots />
							<span className="text-xs text-gray-600">Writing...</span>
						</div>
					</div>
				)}
			</React.Fragment>
		);
	};

	return (
		<div
			className={clsx(
				'flex flex-col mt-6 gap-3 h-full overflow-y-auto overflow-hidden scroll-custom px-2',
				className
			)}
			onClick={handleClick}
			ref={containerRef}
		>
			{!hideHeaderDate && date && (
				<Typography
					type="regular-14"
					className="text-opacity-black text-center mb-2"
				>
					{formatTimeWithToday(date.toString())}
				</Typography>
			)}

			{messages?.map((item, index) => renderMessage(item, index))}

			{isLoading && !streamingMessageId && (
				<div className="p-3 rounded-2xl w-fit rounded-tl-none bg-green-20">
					<LoaderDots />
				</div>
			)}
		</div>
	);
};
